import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { InfoDialogComponent, InfoDialogData, LoadingDialogComponent, LoadingDialogData } from '@shared/ui';
import { AuthService } from '@telespot/web-core';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { RoleData, RoleSwitchersComponent } from '@telespot/authentication/ui';
import { UserCreatedStatus } from '../../../stores/user/affiliated-user.store';
import { WorkspaceItem } from '@telespot/domain';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { WorkspaceSelectorComponent } from '../../workspace/widgets/workspace-selector/workspace-selector.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { UserCreatorFacade } from './user-creator.facade';
import { userCreatorFacadeProviders } from './user-creator.facade.providers';

@Component({
  selector: 'ts-user-creator',
  templateUrl: './user-creator.component.html',
  styleUrls: ['./user-creator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...userCreatorFacadeProviders()],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    RoleSwitchersComponent,
    WorkspaceSelectorComponent,
  ],
})
export class UserCreatorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public initialWorkspaceSearchTerm: string | undefined;
  public hidePassword = true;

  constructor(
    public readonly facade: UserCreatorFacade,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly _dialog: MatDialog
  ) {}

  public ngOnInit() {
    const org = this.authService.currentOrganizationValue;

    // Capture the initial search term value
    this.facade.workspaces$.pipe(take(1)).subscribe(() => {
      // Search term is captured after workspaces store is initialized
      this.initialWorkspaceSearchTerm = '';
    });

    // Initialize facade with organization context
    this.facade.initialize({ organizationId: org.id, maxUsers: org.license.maxUsers });

    // The screen is reused for two entry points ("New User" and "Add existing
    // user"); the mode is preset from the query param
    if (this.route.snapshot.queryParamMap.get('mode') === 'existing') {
      this.facade.toggleAddExistingUser(true);
    }
  }

  public get roles$(): Observable<RoleData[]> {
    return this.facade.roles$;
  }

  public get loadingRoles$(): Observable<boolean> {
    return this.facade.rolesLoading$;
  }

  public ngOnDestroy() {
    this.destroy$.next();
  }

  public updateEmail(email: string) {
    this.facade.setFormEmail(email);
  }

  public loadMoreWorkspaces(hasMoreItems: boolean) {
    this.facade.loadMoreWorkspaces(hasMoreItems);
  }

  public loadAllWorkspaces() {
    this.facade.loadAllWorkspaces();
  }

  public searchWorkspace(term: string) {
    this.facade.searchWorkspace(term);
  }

  public grantRole(role: RoleData) {
    this.facade.grantRole(role);
  }

  public revokeRole(role: RoleData) {
    this.facade.revokeRole(role);
  }

  public createUser() {
    const data: LoadingDialogData = {
      message: 'dialog.creating_user',
      icon: 'dialog.creating_user',
    };

    const dialog = this._dialog.open(LoadingDialogComponent, { data, disableClose: true });

    this.facade.userCreationStatus$
      .pipe(
        filter((status) => status !== UserCreatedStatus.NOT_CREATED),
        takeUntil(dialog.afterClosed()),
        takeUntil(this.destroy$)
      )
      .subscribe((status) => {
        dialog.close();
        if (status === UserCreatedStatus.CREATED) {
          this.openSuccessInfoDialog();
        } else {
          this.openUserAssignedToOrganizationInfoDialog();
        }
      });

    this.facade.error$
      .pipe(
        filter((error) => !!error),
        takeUntil(dialog.afterClosed()),
        takeUntil(this.destroy$)
      )
      .subscribe((error) => {
        dialog.close();
        this.openErrorInfoDialog(error);
      });

    dialog
      .afterOpened()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.facade.createAndAffiliateUser());
  }

  public toggleRegisterWithoutEmail(value: boolean): void {
    this.facade.toggleRegisterWithoutEmail(value);
  }

  public toggleAddExistingUser(value: boolean): void {
    this.facade.toggleAddExistingUser(value);
  }

  public togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  public addToWorkspaces(workspaces: WorkspaceItem[]) {
    this.facade.addToWorkspaces(workspaces);
  }

  public removeFromWorkspaces(workspaces: WorkspaceItem[]) {
    this.facade.removeFromWorkspaces(workspaces);
  }

  public navigateToUsers() {
    this.router.navigateByUrl('users');
  }

  private openSuccessInfoDialog() {
    this.openInfoDialog(
      {
        title: 'dialog.user_create_success_title',
        text: 'dialog.user_create_success_text',
        buttonText: 'button.back_to_user',
      },
      false
    );
  }

  private openUserAssignedToOrganizationInfoDialog() {
    this.openInfoDialog(
      {
        title: 'dialog.user_assigned_to_organization_title',
        text: 'dialog.user_assigned_to_organization_text',
        buttonText: 'button.back_to_user',
      },
      false
    );
  }

  private openErrorInfoDialog(error?: Error | string) {
    this.openInfoDialog(
      {
        title: 'dialog.user_create_error_title',
        text: error instanceof Error ? error.message : error,
        buttonText: 'button.back_to_user',
      },
      true
    );
  }

  private openInfoDialog(data: InfoDialogData, allowClose: boolean) {
    const dialogRef = this._dialog.open(InfoDialogComponent, { data, disableClose: true });

    dialogRef
      .afterClosed()
      .pipe(
        filter(({ dismissed }) => !dismissed || !allowClose),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.navigateToUsers());
  }
}
